export interface DestinationDetail {
  slug: string;
  name: string;
  themeId: string;
  tagline: string;
  overview: string;
  highlights: { title: string; description: string }[];
  bestTimeToVisit: string;
  idealFor: string[];
  travelTips: string[];
  nearestAirport: string;
}

export const destinationDetails: DestinationDetail[] = [
  // ─── Ayurveda ───
  {
    slug: "kumarakom",
    name: "Kumarakom",
    themeId: "ayurveda",
    tagline: "Backwater bliss meets ancient healing",
    overview:
      "Nestled on the banks of Vembanad Lake, Kumarakom is a serene cluster of islands famous for its backwaters and world-class Ayurveda resorts. The peaceful waterways, lush paddy fields, and unhurried pace of life create the perfect environment for deep healing and rejuvenation.",
    highlights: [
      { title: "Lakeside Ayurveda Retreats", description: "Stay at heritage resorts offering Panchakarma, Abhyanga, and Shirodhara treatments with lake views." },
      { title: "Backwater Houseboat Cruises", description: "Drift through tranquil canals on a traditional kettuvallam while enjoying organic Kerala cuisine." },
      { title: "Kumarakom Bird Sanctuary", description: "Spot migratory birds like egrets, herons, and kingfishers in this 14-acre haven." },
      { title: "Village Life Experiences", description: "Paddle through canoe trails, visit toddy tappers, and learn traditional fishing techniques." },
    ],
    bestTimeToVisit: "September – March (cool, dry weather ideal for treatments)",
    idealFor: ["Ayurveda detox", "Couples retreats", "Nature lovers"],
    travelTips: [
      "Book Ayurveda packages of 7–14 days for best results",
      "Carry light cotton clothing suitable for Kerala's humidity",
      "Try the traditional Sadhya feast on a banana leaf",
    ],
    nearestAirport: "Cochin International Airport (75 km)",
  },
  {
    slug: "thiruvananthapuram-kovalam",
    name: "Thiruvananthapuram & Kovalam",
    themeId: "ayurveda",
    tagline: "Where royal heritage meets coastal Ayurveda",
    overview:
      "Kerala's capital city and its famous beach suburb Kovalam together offer an authentic Ayurveda experience backed by centuries of royal patronage. Government-certified Ayurveda centres line the coast, offering treatments with ocean breezes and golden sunsets as a backdrop.",
    highlights: [
      { title: "Certified Ayurveda Centres", description: "Choose from dozens of government-certified treatment centres specialising in chronic ailments and wellness." },
      { title: "Kovalam Beach", description: "The iconic lighthouse beach offers a scenic setting for yoga sessions at sunrise." },
      { title: "Padmanabhaswamy Temple", description: "Visit one of India's richest and most sacred temples with stunning Dravidian architecture." },
      { title: "Napier Museum & Zoo", description: "Explore Indo-Saracenic architecture housing art, archaeology, and natural history collections." },
    ],
    bestTimeToVisit: "October – February (pleasant weather, post-monsoon freshness)",
    idealFor: ["Medical Ayurveda", "Beach wellness", "Cultural exploration"],
    travelTips: [
      "Consult an Ayurveda doctor before choosing treatments",
      "Kovalam can get crowded—book beachfront stays early",
      "Combine with a day trip to Kanyakumari (90 km south)",
    ],
    nearestAirport: "Trivandrum International Airport (6 km)",
  },
  {
    slug: "kottakkal",
    name: "Kottakkal",
    themeId: "ayurveda",
    tagline: "The birthplace of Arya Vaidya traditions",
    overview:
      "Kottakkal is synonymous with authentic Ayurveda, home to the legendary Arya Vaidya Sala founded in 1902. This small town in Malappuram district offers an unfiltered, deeply traditional healing experience far removed from tourist circuits.",
    highlights: [
      { title: "Arya Vaidya Sala", description: "One of the world's most respected Ayurveda institutions, offering inpatient treatment for chronic conditions." },
      { title: "Herbal Gardens", description: "Tour medicinal plant nurseries where over 500 species of healing herbs are cultivated." },
      { title: "Traditional Medicine Making", description: "Witness the ancient process of preparing Ayurvedic oils, kashayams, and lehyams." },
      { title: "Quiet Village Atmosphere", description: "Experience genuine rural Kerala without commercial distractions." },
    ],
    bestTimeToVisit: "June – September (monsoon season is traditionally best for Ayurveda treatments)",
    idealFor: ["Serious Ayurveda seekers", "Chronic condition treatment", "Traditional medicine enthusiasts"],
    travelTips: [
      "Treatments here are clinical—expect a hospital-like setting, not a spa",
      "Minimum 2-week stays recommended for therapeutic results",
      "Carry prescriptions and medical history for consultation",
    ],
    nearestAirport: "Calicut International Airport (50 km)",
  },
  {
    slug: "palakkad",
    name: "Palakkad",
    themeId: "ayurveda",
    tagline: "Gateway to Kerala's green heartland",
    overview:
      "Palakkad (Palghat), nestled at the foot of the Western Ghats, is known for its lush rice fields, ancient fort, and growing number of heritage Ayurveda retreats. The Palakkad Gap brings a unique micro-climate that makes it ideal for Ayurvedic therapies.",
    highlights: [
      { title: "Heritage Ayurveda Homes", description: "Stay in converted ancestral mansions (tharavads) offering personalised treatments." },
      { title: "Palakkad Fort", description: "Explore the 18th-century Tipu Sultan fort surrounded by manicured gardens." },
      { title: "Nelliyampathy Hills", description: "Trek through tea and coffee plantations with panoramic valley views." },
      { title: "Dhoni Waterfalls", description: "A refreshing forest trek leading to a beautiful cascading waterfall." },
    ],
    bestTimeToVisit: "October – March (cool, clear skies)",
    idealFor: ["Heritage stays", "Ayurveda with nature", "Off-beat Kerala"],
    travelTips: [
      "Palakkad is less touristy—great for those seeking solitude",
      "Try the local Palakkad-style sadya (vegetarian feast)",
      "Combine with visits to Silent Valley National Park",
    ],
    nearestAirport: "Coimbatore Airport (55 km)",
  },
  {
    slug: "thrissur",
    name: "Thrissur",
    themeId: "ayurveda",
    tagline: "Cultural capital with healing traditions",
    overview:
      "Known as the cultural capital of Kerala, Thrissur blends rich temple heritage with authentic Ayurveda. Home to the famous Thrissur Pooram festival, the city offers a unique blend of spiritual energy and traditional healing that few places can match.",
    highlights: [
      { title: "Vadakkunnathan Temple", description: "A stunning ancient Shiva temple with remarkable murals and classical architecture." },
      { title: "Kerala Kalamandalam", description: "Visit the renowned academy of Kathakali, Mohiniyattam, and other classical art forms." },
      { title: "Athirappilly Falls", description: "Kerala's Niagara—a majestic 80-foot waterfall surrounded by tropical forest." },
      { title: "Traditional Ayurveda Clinics", description: "Authentic family-run clinics offering Kalari treatments and marma therapy." },
    ],
    bestTimeToVisit: "April – May (for Thrissur Pooram) or October – February (for Ayurveda)",
    idealFor: ["Culture & healing combo", "Art lovers", "Festival enthusiasts"],
    travelTips: [
      "Time your visit with Thrissur Pooram (April/May) for an unforgettable experience",
      "Athirappilly is 60 km away—plan a full day trip",
      "Book Kalari marma treatments with experienced masters",
    ],
    nearestAirport: "Cochin International Airport (58 km)",
  },
  {
    slug: "ashtamudi-aluva",
    name: "Ashtamudi & Aluva",
    themeId: "ayurveda",
    tagline: "Lake serenity and river-side healing",
    overview:
      "Ashtamudi Lake in Kollam and the temple town of Aluva near Kochi offer two contrasting yet complementary Ayurveda experiences. Ashtamudi's eight-armed lake provides waterfront retreats, while Aluva's riverside ashrams focus on spiritual healing alongside Ayurveda.",
    highlights: [
      { title: "Ashtamudi Lake Retreats", description: "Lakefront Ayurveda resorts with panoramic views of Kerala's second-largest lake." },
      { title: "Munroe Island", description: "A hidden gem of narrow canals, Chinese fishing nets, and village life." },
      { title: "Aluva Shiva Temple", description: "An open-air temple on the banks of the Periyar River, famous for Maha Shivaratri." },
      { title: "Backwater Canoe Tours", description: "Navigate through narrow waterways lined with mangroves and coconut palms." },
    ],
    bestTimeToVisit: "September – March (post-monsoon calm on the waters)",
    idealFor: ["Waterfront wellness", "Spiritual Ayurveda", "Quiet getaways"],
    travelTips: [
      "Ashtamudi is the southern starting point of Kerala backwater routes",
      "Aluva is easily accessible as a day trip from Kochi",
      "Try Kollam's cashew and seafood delicacies",
    ],
    nearestAirport: "Trivandrum Airport (70 km for Ashtamudi) / Cochin Airport (20 km for Aluva)",
  },

  // ─── Mindfulness & Silent Retreats ───
  {
    slug: "rishikesh",
    name: "Rishikesh",
    themeId: "mindfulness",
    tagline: "Yoga capital of the world",
    overview:
      "Set where the Ganges emerges from the Himalayas, Rishikesh is the undisputed global centre for yoga and meditation. From the Beatles Ashram to hundreds of yoga schools, this sacred town draws seekers from every corner of the world looking for inner transformation.",
    highlights: [
      { title: "Yoga & Meditation Ashrams", description: "Join multi-day silent retreats or yoga teacher training at world-renowned ashrams." },
      { title: "Ganga Aarti at Triveni Ghat", description: "Experience the mesmerising evening ceremony with fire, chanting, and floating lamps." },
      { title: "Ram Jhula & Laxman Jhula", description: "Walk across iconic suspension bridges with panoramic views of temples and river." },
      { title: "White-Water Rafting", description: "Adventure enthusiasts can raft through Grade III–IV rapids on the Ganges." },
    ],
    bestTimeToVisit: "February – April & September – November (pleasant weather, clear skies)",
    idealFor: ["Yoga practitioners", "Meditation seekers", "Spiritual explorers"],
    travelTips: [
      "Respect local customs—alcohol and non-veg food are prohibited in many areas",
      "Book ashram stays well in advance during peak season",
      "Mornings are best for meditation by the river",
    ],
    nearestAirport: "Jolly Grant Airport, Dehradun (35 km)",
  },
  {
    slug: "dharamshala",
    name: "Dharamshala",
    themeId: "mindfulness",
    tagline: "Himalayan seat of Tibetan wisdom",
    overview:
      "Home to the Dalai Lama and the Tibetan government-in-exile, Dharamshala (and its upper town McLeod Ganj) offers an extraordinary blend of Buddhist philosophy, Himalayan beauty, and profound silence. The energy here is palpable—perfect for deep mindfulness practice.",
    highlights: [
      { title: "Tsuglagkhang Complex", description: "Visit the Dalai Lama's temple, the Tibet Museum, and join public teachings when available." },
      { title: "Vipassana Centres", description: "Enrol in 10-day silent Vipassana courses in serene mountain settings." },
      { title: "Triund Trek", description: "A moderate trek rewarding you with 360° views of the Dhauladhar range." },
      { title: "Tibetan Culture", description: "Explore monasteries, thangka painting workshops, and authentic Tibetan cuisine." },
    ],
    bestTimeToVisit: "March – June & September – November (clear mountain views)",
    idealFor: ["Buddhist meditation", "Mountain mindfulness", "Cultural immersion"],
    travelTips: [
      "Check the Dalai Lama's schedule for public teachings",
      "Carry warm layers—temperatures drop significantly at night",
      "McLeod Ganj is walkable; stay close to the main temple for convenience",
    ],
    nearestAirport: "Gaggal Airport, Kangra (15 km)",
  },
  {
    slug: "mysuru",
    name: "Mysuru",
    themeId: "mindfulness",
    tagline: "Royal city of Ashtanga yoga",
    overview:
      "Mysuru (Mysore) is the birthplace of Ashtanga Yoga, made famous by the legendary guru Sri K. Pattabhi Jois. Beyond yoga, the city enchants with its royal palaces, fragrant sandalwood, jasmine-draped markets, and a gracious old-world charm.",
    highlights: [
      { title: "Ashtanga Yoga Shalas", description: "Train at authentic shalas where the Ashtanga tradition has been taught for decades." },
      { title: "Mysore Palace", description: "One of India's most visited monuments—a stunning Indo-Saracenic masterpiece." },
      { title: "Chamundi Hills", description: "Climb 1,000 steps to the hilltop temple for sunrise meditation and city views." },
      { title: "Devaraja Market", description: "A sensory feast of flowers, spices, sandalwood, and silk." },
    ],
    bestTimeToVisit: "October – February (cool, pleasant weather) or during Dasara festival",
    idealFor: ["Ashtanga yoga", "Cultural wellness", "Heritage exploration"],
    travelTips: [
      "Most yoga shalas require a minimum 1-month commitment",
      "Visit during Dasara (October) for the spectacular 10-day festival",
      "Mysore pak and filter coffee are must-try local treats",
    ],
    nearestAirport: "Mysore Airport (limited flights) or Bengaluru Airport (170 km)",
  },
  {
    slug: "auroville",
    name: "Auroville",
    themeId: "mindfulness",
    tagline: "The city of dawn and conscious living",
    overview:
      "Auroville is a unique international township near Pondicherry, founded on the vision of human unity and spiritual evolution. Centred around the golden Matrimandir, this community offers meditation, sustainable living workshops, and a profoundly peaceful atmosphere for inner exploration.",
    highlights: [
      { title: "Matrimandir", description: "Meditate inside the stunning golden sphere—the spiritual heart of Auroville." },
      { title: "Community Living", description: "Experience sustainable practices, organic farming, and eco-architecture." },
      { title: "Art & Craft Studios", description: "Visit pottery workshops, handmade paper units, and incense-making studios." },
      { title: "Pondicherry Day Trip", description: "Explore nearby French Quarter, Aurobindo Ashram, and Promenade Beach." },
    ],
    bestTimeToVisit: "November – March (pleasant coastal weather)",
    idealFor: ["Conscious living seekers", "Meditation", "Sustainable travel"],
    travelTips: [
      "Matrimandir visits require advance booking through the visitor centre",
      "Rent a bicycle—Auroville is best explored on two wheels",
      "Carry cash; many places don't accept cards",
    ],
    nearestAirport: "Chennai Airport (150 km) or Pondicherry (12 km, limited flights)",
  },
  {
    slug: "igatpuri",
    name: "Igatpuri",
    themeId: "mindfulness",
    tagline: "India's Vipassana heartland",
    overview:
      "Igatpuri in Maharashtra's Sahyadri hills is home to one of the world's largest Vipassana meditation centres—Dhamma Giri. Surrounded by misty green hills and cascading waterfalls, this small town offers a powerful setting for deep silent meditation practice.",
    highlights: [
      { title: "Dhamma Giri", description: "The global headquarters of S.N. Goenka's Vipassana tradition, offering free 10-day courses." },
      { title: "Bhandardara Lake", description: "A pristine lake surrounded by mountains—perfect for post-retreat contemplation." },
      { title: "Kalsubai Peak", description: "Trek to Maharashtra's highest point (1,646m) for spectacular sunrise views." },
      { title: "Monsoon Waterfalls", description: "Dozens of seasonal waterfalls transform the landscape during July–September." },
    ],
    bestTimeToVisit: "July – September (lush monsoon beauty) or October – February (clear skies)",
    idealFor: ["Vipassana meditation", "First-time meditators", "Nature & silence"],
    travelTips: [
      "Vipassana courses are donation-based and must be booked months in advance",
      "The 10-day course is intensive—prepare mentally for complete silence",
      "Igatpuri is a quick 2-hour train ride from Mumbai",
    ],
    nearestAirport: "Mumbai Airport (120 km) or Nashik (45 km)",
  },
  {
    slug: "bodh-gaya",
    name: "Bodh Gaya",
    themeId: "mindfulness",
    tagline: "Where the Buddha attained enlightenment",
    overview:
      "Bodh Gaya in Bihar is the most sacred site in Buddhism—where Siddhartha Gautama sat under the Bodhi Tree and attained enlightenment 2,500 years ago. The Mahabodhi Temple complex radiates an extraordinary peace that draws pilgrims and meditators from across the globe.",
    highlights: [
      { title: "Mahabodhi Temple", description: "A UNESCO World Heritage Site with the sacred Bodhi Tree and the diamond throne (Vajrasana)." },
      { title: "International Monasteries", description: "Visit Thai, Japanese, Tibetan, and Bhutanese monasteries, each with distinct architecture." },
      { title: "Meditation Under the Bodhi Tree", description: "Sit in meditation in the very spot where the Buddha found enlightenment." },
      { title: "Sujata Kuti & Dungeshwari Caves", description: "Explore nearby sites connected to the Buddha's journey before enlightenment." },
    ],
    bestTimeToVisit: "October – March (comfortable temperatures for outdoor meditation)",
    idealFor: ["Buddhist pilgrimage", "Deep meditation", "Spiritual history"],
    travelTips: [
      "Dress modestly and remove shoes at temple complexes",
      "Early morning (5 AM) is the most peaceful time at the Bodhi Tree",
      "Combine with a visit to Rajgir and Nalanda (nearby Buddhist sites)",
    ],
    nearestAirport: "Gaya Airport (17 km)",
  },
  {
    slug: "tawang",
    name: "Tawang",
    themeId: "mindfulness",
    tagline: "Monastic peace in the clouds",
    overview:
      "Perched at 10,000 feet in Arunachal Pradesh, Tawang is home to India's largest Buddhist monastery and the birthplace of the 6th Dalai Lama. The journey itself—through Sela Pass and misty mountain roads—is a meditative experience. Tawang offers solitude that few places on Earth can match.",
    highlights: [
      { title: "Tawang Monastery", description: "The 400-year-old Galden Namgey Lhatse is India's largest monastery, housing priceless Buddhist manuscripts." },
      { title: "Sela Pass", description: "Cross the breathtaking 13,700-foot pass with its sacred lake and prayer flags." },
      { title: "Madhuri Lake", description: "A stunning glacial lake surrounded by snow-capped peaks and pristine wilderness." },
      { title: "War Memorial", description: "Pay respects at the poignant memorial commemorating the 1962 conflict." },
    ],
    bestTimeToVisit: "March – June & September – October (roads open, clear weather)",
    idealFor: ["Remote meditation", "Buddhist heritage", "Adventure seekers"],
    travelTips: [
      "Inner Line Permit (ILP) required for Indian citizens; Protected Area Permit for foreigners",
      "Acclimatise gradually—altitude sickness is common",
      "Road conditions can be challenging; allow extra travel days",
    ],
    nearestAirport: "Tezpur Airport (320 km) or Guwahati Airport (480 km)",
  },
  {
    slug: "spiti-valley",
    name: "Spiti Valley",
    themeId: "mindfulness",
    tagline: "The middle land between earth and sky",
    overview:
      "Spiti Valley in Himachal Pradesh is a cold desert mountain valley sitting at 12,500 feet. With ancient Buddhist monasteries perched on barren cliffs, a stark lunar landscape, and the silence of vast emptiness, Spiti strips away all distractions and leaves you face-to-face with yourself.",
    highlights: [
      { title: "Key Monastery", description: "A 1,000-year-old monastery dramatically perched on a hilltop with valley views." },
      { title: "Chandratal Lake", description: "The crescent-shaped 'Moon Lake' at 14,000 feet, reflecting the sky in perfect stillness." },
      { title: "Dhankar Monastery", description: "A cliffside monastery with a meditation cave overlooking the confluence of Spiti and Pin rivers." },
      { title: "Stargazing", description: "Some of the clearest night skies in the world—perfect for contemplative stargazing." },
    ],
    bestTimeToVisit: "June – September (only window when roads are fully open)",
    idealFor: ["Solitude seekers", "High-altitude meditation", "Photography"],
    travelTips: [
      "Carry altitude sickness medication and acclimatise slowly",
      "There's limited mobile network—embrace the digital detox",
      "Pack warm clothing even in summer; nights can drop below freezing",
    ],
    nearestAirport: "Kullu-Manali Airport (200 km) or Shimla (412 km)",
  },

  // ─── Snow Healing ───
  {
    slug: "himachal-pradesh",
    name: "Himachal Pradesh",
    themeId: "snow-healing",
    tagline: "Where snow-clad peaks heal the soul",
    overview:
      "Himachal Pradesh's snow-covered landscapes—from Shimla to Manali to the remote Lahaul-Spiti region—offer a powerful form of natural healing. The crisp mountain air, pristine white vistas, and cosy mountain lodges provide the perfect antidote to modern-day stress.",
    highlights: [
      { title: "Solang Valley", description: "Ski slopes and snow activities surrounded by magnificent Himalayan peaks." },
      { title: "Old Manali", description: "Charming cafés, riverside walks, and mountain yoga retreats." },
      { title: "Shimla's Colonial Charm", description: "Walk the Ridge, visit Christ Church, and enjoy heritage railway journeys." },
      { title: "Hot Springs of Manikaran", description: "Soak in natural thermal springs believed to have healing properties." },
    ],
    bestTimeToVisit: "December – February (peak snow) or March – May (melting snow, clear views)",
    idealFor: ["Snow therapy", "Mountain wellness", "Winter getaways"],
    travelTips: [
      "Layer your clothing—temperatures fluctuate dramatically",
      "Roads may close during heavy snowfall; keep flexible plans",
      "Carry moisturiser and sunscreen—cold, dry air and UV at altitude",
    ],
    nearestAirport: "Kullu-Manali Airport or Shimla Airport",
  },
  {
    slug: "kashmir",
    name: "Kashmir",
    themeId: "snow-healing",
    tagline: "Paradise on Earth draped in snow",
    overview:
      "Kashmir, nestled in the northernmost reaches of India, is a land of breathtaking beauty where snow-capped peaks reflect in pristine lakes. From the iconic Dal Lake houseboats to the powder-white slopes of Gulmarg, Kashmir offers a healing experience wrapped in nature's most magnificent winter canvas.",
    highlights: [
      { title: "Dal Lake & Houseboats", description: "Stay in ornate wooden houseboats on the mirror-still waters of Dal Lake surrounded by snow-dusted mountains." },
      { title: "Gulmarg Ski Resort", description: "Asia's highest gondola ride and world-class skiing on pristine powder snow at 13,780 feet." },
      { title: "Mughal Gardens", description: "Stroll through the terraced Shalimar Bagh and Nishat Bagh, stunning even under a blanket of snow." },
      { title: "Pahalgam Valley", description: "A serene valley of pine forests, glacial streams, and meadows perfect for reflective walks." },
    ],
    bestTimeToVisit: "December – February (peak snow) or March – April (snow melting, spring blossoms)",
    idealFor: ["Snow therapy", "Romantic getaways", "Skiing & adventure"],
    travelTips: [
      "Book houseboats in advance during winter season",
      "Layer clothing and carry thermal wear for sub-zero temperatures",
      "Try Kashmiri wazwan cuisine and kehwa tea for warmth",
    ],
    nearestAirport: "Sheikh ul-Alam International Airport, Srinagar",
  },
  {
    slug: "uttarakhand",
    name: "Uttarakhand",
    themeId: "snow-healing",
    tagline: "Dev Bhoomi—Land of the Gods in white",
    overview:
      "Uttarakhand, the sacred land of the Gods, transforms into a winter wonderland with snow-laden peaks, frozen waterfalls, and serene hill stations. From the charming streets of Mussoorie to the alpine meadows of Auli, this Himalayan state offers snow healing intertwined with spiritual energy.",
    highlights: [
      { title: "Auli Ski Resort", description: "Ski down pristine slopes with panoramic views of Nanda Devi and surrounding Himalayan peaks." },
      { title: "Mussoorie in Winter", description: "The Queen of Hills draped in snow, offering colonial-era charm and stunning valley views." },
      { title: "Chopta–Tungnath Trek", description: "Trek to the world's highest Shiva temple through snow-covered rhododendron forests." },
      { title: "Nainital Lake", description: "A serene lake town surrounded by snow-dusted hills, perfect for contemplative walks." },
    ],
    bestTimeToVisit: "December – February (peak snow and winter sports) or October – November (early snow, fewer crowds)",
    idealFor: ["Snow trekking", "Spiritual snow retreats", "Winter sports"],
    travelTips: [
      "Auli requires advance booking for ski equipment and instructors",
      "Roads to higher areas may close during heavy snowfall—keep plans flexible",
      "Carry altitude sickness medication for treks above 10,000 feet",
    ],
    nearestAirport: "Jolly Grant Airport, Dehradun (for Mussoorie/Auli) or Pantnagar Airport (for Nainital)",
  },
  {
    slug: "sikkim",
    name: "Sikkim",
    themeId: "snow-healing",
    tagline: "Mystical Himalayan jewel of the northeast",
    overview:
      "Sikkim, India's smallest state, packs an extraordinary punch with its snow-capped peaks, ancient monasteries, and pristine landscapes. Dominated by the majestic Kanchenjunga, Sikkim offers a deeply restorative experience where Buddhist spirituality meets Himalayan snow healing.",
    highlights: [
      { title: "Kanchenjunga Views from Pelling", description: "Wake up to the world's third-highest peak glowing golden at sunrise from cosy mountain lodges." },
      { title: "Tsomgo Lake", description: "A sacred glacial lake at 12,310 feet, frozen in winter and surrounded by snow-covered slopes." },
      { title: "Rumtek Monastery", description: "One of the most significant Tibetan Buddhist centres outside Tibet, radiating peace and devotion." },
      { title: "Yumthang Valley", description: "The Valley of Flowers transforms into a pristine snowscape in winter with hot springs nearby." },
    ],
    bestTimeToVisit: "December – February (snow-covered landscapes) or March – May (rhododendron blooms with lingering snow)",
    idealFor: ["Mountain meditation", "Buddhist heritage", "Snow photography"],
    travelTips: [
      "Inner Line Permits required for Tsomgo Lake and Nathula Pass",
      "Acclimatise at Gangtok (5,400 ft) before heading to higher altitudes",
      "Try momos, thukpa, and fermented millet drink (chang) for authentic local flavour",
    ],
    nearestAirport: "Pakyong Airport, Gangtok (30 km) or Bagdogra Airport (125 km)",
  },
  {
    slug: "arunachal-pradesh",
    name: "Arunachal Pradesh",
    themeId: "snow-healing",
    tagline: "Land of the rising sun and eternal snows",
    overview:
      "Arunachal Pradesh, India's northeasternmost frontier, is a land of untouched wilderness where snow-covered peaks meet ancient tribal cultures. From the sacred Gorichen peak to remote mountain passes, this state offers a raw, unfiltered snow-healing experience far from the beaten path.",
    highlights: [
      { title: "Sela Pass", description: "Cross the breathtaking 13,700-foot pass draped in snow with its sacred frozen lake and prayer flags." },
      { title: "Gorichen Peak Views", description: "Witness Arunachal's highest peak (21,300 ft) from Jang and Thembang villages." },
      { title: "Dirang Valley", description: "A picturesque valley with hot springs, apple orchards, and snow-dusted mountains." },
      { title: "Tribal Heritage", description: "Experience the unique cultures of Monpa, Sherdukpen, and other indigenous communities." },
    ],
    bestTimeToVisit: "November – February (snow on higher passes) or March – April (snow melting, clear skies)",
    idealFor: ["Off-beat snow travel", "Tribal culture", "Remote healing"],
    travelTips: [
      "Inner Line Permit (ILP) required for all Indian citizens; Protected Area Permit for foreigners",
      "Road conditions are challenging—allow extra travel days and hire experienced drivers",
      "Limited mobile connectivity—embrace the digital detox",
    ],
    nearestAirport: "Tezpur Airport (for western Arunachal) or Dibrugarh Airport (for eastern Arunachal)",
  },
  {
    slug: "ladakh",
    name: "Ladakh",
    themeId: "snow-healing",
    tagline: "The last Shangri-La of frozen silence",
    overview:
      "Ladakh, the high-altitude desert kingdom, is a land of stark, breathtaking beauty where snow-capped peaks tower over ancient monasteries and frozen turquoise lakes. The silence of its vast emptiness, combined with Buddhist wisdom and extreme landscapes, creates one of the most profound snow-healing experiences on Earth.",
    highlights: [
      { title: "Pangong Tso Lake", description: "A surreal, colour-shifting lake stretching across the India-China border, frozen solid in winter." },
      { title: "Chadar Trek", description: "Walk on the frozen Zanskar River—one of the world's most extraordinary winter treks." },
      { title: "Thiksey & Hemis Monasteries", description: "Visit stunning Tibetan Buddhist monasteries perched on hilltops with Himalayan backdrops." },
      { title: "Nubra Valley & Khardung La", description: "Cross the world's highest motorable pass and explore the surreal sand dunes of Nubra." },
    ],
    bestTimeToVisit: "January – February (Chadar Trek, frozen lakes) or June – September (accessible roads, milder weather)",
    idealFor: ["Extreme snow healing", "Buddhist meditation", "Adventure trekking"],
    travelTips: [
      "Acclimatise in Leh for at least 2 days before venturing to higher altitudes",
      "Carry high-altitude medication and stay well-hydrated",
      "Winter visits require serious preparation—sub-zero temperatures reach -30°C",
    ],
    nearestAirport: "Kushok Bakula Rimpochee Airport, Leh",
  },
  {
    slug: "darjeeling",
    name: "Darjeeling",
    themeId: "forest-healing",
    tagline: "Queen of the Hills with Kanchenjunga views",
    overview:
      "Darjeeling in West Bengal offers a magical combination of tea gardens, Himalayan panoramas, and colonial hill-station charm. With views of the mighty Kanchenjunga and a pace of life set by the toy train's gentle rhythm, Darjeeling heals through beauty, tea, and mountain air.",
    highlights: [
      { title: "Tiger Hill Sunrise", description: "Watch the sun paint Kanchenjunga gold from this iconic viewpoint at 8,000 feet." },
      { title: "Tea Garden Tours", description: "Walk through emerald-green estates and learn the art of Darjeeling tea making." },
      { title: "Darjeeling Himalayan Railway", description: "Ride the UNESCO-listed toy train through loops, zigzags, and misty mountain tunnels." },
      { title: "Batasia Loop & Peace Pagoda", description: "Visit the war memorial garden and the Japanese-built Peace Pagoda for contemplation." },
    ],
    bestTimeToVisit: "October – December (crisp air, clear mountain views) or March – May (spring blooms)",
    idealFor: ["Tea lovers", "Mountain healing", "Heritage enthusiasts"],
    travelTips: [
      "Book Tiger Hill jeep rides the evening before for the 4 AM departure",
      "Carry warm jackets even in spring—mornings are chilly",
      "Try momos and thukpa at local Tibetan eateries",
    ],
    nearestAirport: "Bagdogra Airport (70 km)",
  },

  // ─── Forest Healing ───
  {
    slug: "meghalaya",
    name: "Meghalaya",
    themeId: "forest-healing",
    tagline: "Abode of clouds and living roots",
    overview:
      "Meghalaya—literally 'the abode of clouds'—is a land of living root bridges, crystal-clear rivers, and some of the wettest places on Earth. Its ancient Khasi and Jaintia forests offer an immersive forest-bathing experience that reconnects you with nature at the deepest level.",
    highlights: [
      { title: "Living Root Bridges", description: "Walk across bridges grown over centuries by training tree roots—a living testament to human-nature harmony." },
      { title: "Dawki River", description: "Boat on the impossibly clear Umngot River where boats appear to float on air." },
      { title: "Mawsynram & Cherrapunji", description: "Visit the wettest places on Earth with countless waterfalls and dramatic gorges." },
      { title: "Sacred Groves", description: "Explore ancient forest patches preserved for centuries by indigenous communities." },
    ],
    bestTimeToVisit: "October – May (post-monsoon freshness with waterfalls still flowing)",
    idealFor: ["Forest bathing", "Eco-tourism", "Adventure trekking"],
    travelTips: [
      "Carry rain gear year-round—Meghalaya earns its name",
      "The trek to Double Decker Root Bridge requires moderate fitness",
      "Respect indigenous customs in Khasi villages",
    ],
    nearestAirport: "Shillong Airport (30 km) or Guwahati Airport (130 km)",
  },
  {
    slug: "chikmagalur",
    name: "Chikmagalur",
    themeId: "forest-healing",
    tagline: "Coffee country in the Western Ghats",
    overview:
      "Chikmagalur in Karnataka is where India's coffee story began. Surrounded by the Western Ghats' lush forests, misty peaks, and cascading streams, it offers a gentle form of forest healing through plantation stays, nature walks, and the aroma of freshly brewed coffee.",
    highlights: [
      { title: "Coffee Plantation Stays", description: "Wake up to birdsong and the scent of coffee blossoms in heritage plantation bungalows." },
      { title: "Mullayanagiri Peak", description: "Trek to Karnataka's highest peak (1,930m) through shola grasslands and forests." },
      { title: "Bhadra Wildlife Sanctuary", description: "Spot elephants, tigers, and hornbills in this rich Western Ghats reserve." },
      { title: "Hebbe Falls", description: "A two-tiered waterfall hidden deep within the coffee estate forests." },
    ],
    bestTimeToVisit: "September – February (cool, post-monsoon greenery)",
    idealFor: ["Coffee lovers", "Forest therapy", "Birding"],
    travelTips: [
      "Rent a jeep for off-road access to waterfalls and peaks",
      "Leeches are common during monsoons—carry salt and leech socks",
      "Buy estate-fresh coffee beans as souvenirs",
    ],
    nearestAirport: "Mangalore Airport (150 km) or Bengaluru Airport (245 km)",
  },
  {
    slug: "coorg",
    name: "Coorg",
    themeId: "forest-healing",
    tagline: "Scotland of India in emerald green",
    overview:
      "Coorg (Kodagu) in Karnataka is a lush hill district known as the Scotland of India. With its coffee and spice plantations, misty hills, and the warrior Kodava culture, Coorg offers forest healing wrapped in warmth, hospitality, and the fragrance of cardamom and pepper.",
    highlights: [
      { title: "Abbey Falls", description: "A picturesque waterfall set amidst coffee and spice plantations." },
      { title: "Dubare Elephant Camp", description: "Interact with elephants at a forest department camp by the Kaveri River." },
      { title: "Raja's Seat", description: "A garden viewpoint where Kodagu kings once watched sunsets over misty valleys." },
      { title: "Nagarhole National Park", description: "Safari through one of India's best tiger reserves bordering Coorg." },
    ],
    bestTimeToVisit: "October – March (cool and dry) or June – September (lush monsoon green)",
    idealFor: ["Plantation retreats", "Wildlife", "Family wellness"],
    travelTips: [
      "Try the famous Coorgi pandi curry (pork) and akki rotti",
      "Homestays offer the most authentic Coorg experience",
      "Carry warm clothing for early morning nature walks",
    ],
    nearestAirport: "Mysore Airport (120 km) or Mangalore Airport (160 km)",
  },
  {
    slug: "munnar",
    name: "Munnar",
    themeId: "forest-healing",
    tagline: "Emerald tea hills of Kerala",
    overview:
      "Munnar in Kerala's Idukki district is a breathtaking hill station draped in endless tea plantations. At 1,600 metres above sea level, its cool climate, misty mornings, and rolling green carpets offer a deeply restorative forest-and-mountain healing experience.",
    highlights: [
      { title: "Tea Museum", description: "Learn about Munnar's 150-year tea heritage and sample fresh varieties." },
      { title: "Eravikulam National Park", description: "Home to the endangered Nilgiri tahr; trek through grasslands with mountain views." },
      { title: "Top Station", description: "The highest point on the Munnar–Kodaikanal road with views stretching into Tamil Nadu." },
      { title: "Neelakurinji Blooms", description: "Once every 12 years, these blue flowers carpet the hillsides (next bloom: 2030)." },
    ],
    bestTimeToVisit: "September – May (avoid heavy monsoon months June–August)",
    idealFor: ["Tea lovers", "Mountain retreats", "Photography"],
    travelTips: [
      "Roads are winding—carry motion sickness medication if needed",
      "Mornings in the tea gardens are magical for photography",
      "Book plantation bungalow stays for the full experience",
    ],
    nearestAirport: "Cochin International Airport (110 km)",
  },

  // ─── Coastal Healing ───
  {
    slug: "gokarna",
    name: "Gokarna",
    themeId: "coastal-healing",
    tagline: "Sacred shores where temples meet the sea",
    overview:
      "Gokarna on Karnataka's coast is a rare blend of pilgrimage town and pristine beach paradise. Unlike its famous neighbour Goa, Gokarna retains an unspoiled, meditative quality—perfect for those seeking coastal healing through yoga retreats, beach walks, and temple visits.",
    highlights: [
      { title: "Om Beach", description: "Named for its Om-shaped coastline, this beach hosts yoga retreats and sunset gatherings." },
      { title: "Mahabaleshwar Temple", description: "One of the seven sacred pilgrimage sites (mukti sthalas) dedicated to Lord Shiva." },
      { title: "Beach Trek", description: "Hike the cliff trail connecting Gokarna Beach, Kudle, Om, Half Moon, and Paradise beaches." },
      { title: "Beach Yoga & Meditation", description: "Join sunrise yoga sessions on quiet beaches with the sound of waves as your soundtrack." },
    ],
    bestTimeToVisit: "October – March (dry, pleasant weather)",
    idealFor: ["Beach yoga", "Budget wellness", "Quiet beach retreats"],
    travelTips: [
      "Half Moon and Paradise beaches are remote—carry water and snacks",
      "Respect the temple town's conservative culture in dress and behaviour",
      "Gokarna is much quieter than Goa—perfect if you want peace",
    ],
    nearestAirport: "Dabolim Airport, Goa (140 km) or Hubli Airport (160 km)",
  },
  {
    slug: "andaman",
    name: "Andaman",
    themeId: "coastal-healing",
    tagline: "Turquoise paradise in the Bay of Bengal",
    overview:
      "The Andaman Islands are India's most remote tropical paradise—crystal-clear waters, untouched coral reefs, and dense tropical forests. Far removed from mainland chaos, these islands offer deep coastal healing through snorkelling, beach meditation, and the pure rhythm of island life.",
    highlights: [
      { title: "Radhanagar Beach", description: "Consistently rated Asia's best beach—powdery white sand and turquoise water." },
      { title: "Coral Reef Snorkelling", description: "Explore vibrant coral gardens at Elephant Beach and Neil Island." },
      { title: "Cellular Jail", description: "Visit the historic colonial prison and attend the moving sound-and-light show." },
      { title: "Bioluminescent Beaches", description: "Witness the magical glow of plankton lighting up the shoreline at night." },
    ],
    bestTimeToVisit: "November – April (calm seas, clear visibility for water activities)",
    idealFor: ["Island healing", "Snorkelling & diving", "Digital detox"],
    travelTips: [
      "Book inter-island ferries in advance during peak season",
      "Carry reef-safe sunscreen to protect the delicate coral ecosystem",
      "Limited ATMs on smaller islands—carry sufficient cash",
    ],
    nearestAirport: "Veer Savarkar International Airport, Port Blair",
  },
  {
    slug: "goa",
    name: "Goa",
    themeId: "coastal-healing",
    tagline: "Sun, sand, and soulful renewal",
    overview:
      "Beyond its party reputation, Goa's quieter stretches—South Goa's Palolem, Agonda, and the hinterland villages—offer a deeply healing coastal experience. Portuguese heritage, Ayurveda spas, yoga retreats, and long golden beaches create a unique wellness cocktail.",
    highlights: [
      { title: "South Goa Beaches", description: "Palolem, Agonda, and Cola Beach offer serene, crowd-free stretches of sand." },
      { title: "Yoga & Wellness Retreats", description: "Dozens of retreat centres in Mandrem, Assagao, and Palolem offer yoga and Ayurveda." },
      { title: "Old Goa Churches", description: "UNESCO World Heritage basilicas showcasing stunning Portuguese Baroque architecture." },
      { title: "Spice Plantations", description: "Tour organic spice farms in the Western Ghats foothills with traditional Goan lunch." },
    ],
    bestTimeToVisit: "November – February (perfect beach weather, festive season)",
    idealFor: ["Beach wellness", "Yoga retreats", "Cultural exploration"],
    travelTips: [
      "Stay in South Goa for a quieter, more healing experience",
      "Rent a scooter to explore hidden beaches and villages",
      "Try Goan fish curry, bebinca, and feni for authentic local flavours",
    ],
    nearestAirport: "Goa International Airport, Dabolim (or new Manohar Airport, Mopa)",
  },
  {
    slug: "lakshadweep",
    name: "Lakshadweep",
    themeId: "coastal-healing",
    tagline: "India's untouched coral paradise",
    overview:
      "Lakshadweep, a chain of 36 pristine coral islands in the Arabian Sea, is India's smallest Union Territory and one of its best-kept secrets. With turquoise lagoons, vibrant coral reefs, and powder-white beaches, these islands offer an unparalleled coastal healing experience far from the mainland bustle.",
    highlights: [
      { title: "Agatti Island Lagoon", description: "A stunning turquoise lagoon perfect for kayaking, swimming, and simply soaking in island serenity." },
      { title: "Coral Reef Diving & Snorkelling", description: "Explore some of India's healthiest coral reefs teeming with tropical fish and sea turtles." },
      { title: "Bangaram Atoll", description: "An uninhabited teardrop-shaped island with pristine beaches and crystal-clear waters." },
      { title: "Glass-Bottom Boat Rides", description: "View the spectacular underwater world without getting wet—ideal for all ages." },
    ],
    bestTimeToVisit: "October – May (calm seas, clear skies, ideal for water activities)",
    idealFor: ["Island healing", "Snorkelling & diving", "Digital detox", "Romantic getaways"],
    travelTips: [
      "Entry permits are mandatory—apply through the Lakshadweep tourism office well in advance",
      "Alcohol is restricted on most islands; only Bangaram allows it",
      "Carry reef-safe sunscreen and biodegradable products to protect the fragile ecosystem",
    ],
    nearestAirport: "Agatti Airport (flights from Kochi)",
  },

  // ─── Spiritual Healing ───
  {
    slug: "varanasi",
    name: "Varanasi",
    themeId: "spiritual-healing",
    tagline: "The eternal city on the Ganges",
    overview:
      "Varanasi is one of the world's oldest continuously inhabited cities and Hinduism's holiest pilgrimage site. Life and death coexist here in an ancient rhythm along the ghats of the Ganges. For the open-minded traveller, Varanasi offers a spiritual intensity that can be profoundly transformative.",
    highlights: [
      { title: "Ganga Aarti at Dashashwamedh Ghat", description: "A grand evening ceremony of fire, drums, and devotion that's unmissable." },
      { title: "Sunrise Boat Ride", description: "Glide past 80+ ghats at dawn as the city awakens with prayer and ritual." },
      { title: "Kashi Vishwanath Temple", description: "One of the 12 Jyotirlingas—the most sacred Shiva temple in India." },
      { title: "Silk Weaving", description: "Visit workshops where artisans create the famous Banarasi silk saris by hand." },
    ],
    bestTimeToVisit: "October – March (cooler weather, festival season) or during Dev Deepawali",
    idealFor: ["Spiritual seekers", "Cultural immersion", "Photography"],
    travelTips: [
      "Hire a local guide to navigate the narrow gali (lanes)",
      "Be prepared for sensory overload—Varanasi is intense",
      "Respect cremation ghats—photography is not permitted at Manikarnika Ghat",
    ],
    nearestAirport: "Lal Bahadur Shastri Airport, Varanasi (25 km)",
  },
  {
    slug: "shirdi",
    name: "Shirdi",
    themeId: "spiritual-healing",
    tagline: "Home of Sai Baba's boundless compassion",
    overview:
      "Shirdi in Maharashtra is one of India's most visited pilgrimage towns, drawing millions who seek the blessings of Sai Baba. The temple complex radiates an atmosphere of unconditional love and surrender, offering spiritual healing through devotion, community, and sacred simplicity.",
    highlights: [
      { title: "Sai Baba Temple", description: "The samadhi mandir where Sai Baba's mortal remains rest, open for darshan throughout the day." },
      { title: "Dwarkamai Mosque", description: "The mosque where Sai Baba lived, housing the sacred dhuni (eternal fire) he maintained." },
      { title: "Chavadi", description: "Where Sai Baba slept on alternate nights—preserved with his personal belongings." },
      { title: "Prasadalaya", description: "The world's largest free kitchen, serving meals to over 40,000 devotees daily." },
    ],
    bestTimeToVisit: "Year-round (temple is always open); October – March for comfortable weather",
    idealFor: ["Devotional healing", "Faith journeys", "Family pilgrimages"],
    travelTips: [
      "Book darshan passes online to avoid long queues",
      "Accommodate at the Sansthan's official guest houses for convenience",
      "Thursday evenings have special celebrations at the temple",
    ],
    nearestAirport: "Shirdi Airport (14 km) or Pune Airport (185 km)",
  },
  {
    slug: "kedarnath",
    name: "Kedarnath",
    themeId: "spiritual-healing",
    tagline: "Sacred shrine at the roof of the world",
    overview:
      "Kedarnath, one of the Char Dham and twelve Jyotirlingas, sits at 11,755 feet in Uttarakhand, surrounded by snow-capped Himalayan peaks. The arduous trek to this ancient Shiva temple is itself a spiritual practice—each step stripping away the mundane and bringing you closer to the divine.",
    highlights: [
      { title: "Kedarnath Temple", description: "A 1,200-year-old stone temple believed to be built by the Pandavas, surviving floods and earthquakes." },
      { title: "Trek from Gaurikund", description: "The 16-km trek passes through forests, waterfalls, and mountain meadows." },
      { title: "Bhairavnath Temple", description: "A cliff-top temple offering stunning views of Kedarnath valley and surrounding peaks." },
      { title: "Chorabari Glacier", description: "Also called Gandhi Sarovar—a glacial lake near the temple at 12,500 feet." },
    ],
    bestTimeToVisit: "May – June & September – October (temple is open only 6 months)",
    idealFor: ["Pilgrimage", "Mountain spirituality", "Trekking devotees"],
    travelTips: [
      "Physical fitness is important—the trek is demanding at high altitude",
      "Helicopter services are available for those unable to trek",
      "Temple closes for winter (November–April) due to heavy snowfall",
    ],
    nearestAirport: "Jolly Grant Airport, Dehradun (250 km)",
  },
  {
    slug: "haridwar",
    name: "Haridwar",
    themeId: "spiritual-healing",
    tagline: "Gateway to the gods on the sacred Ganges",
    overview:
      "Haridwar, where the Ganges descends from the Himalayas to the plains, is one of Hinduism's seven sacred cities. The constant flow of pilgrims, the evening Ganga Aarti at Har Ki Pauri, and the city's ancient spiritual energy make it a powerful destination for inner cleansing and renewal.",
    highlights: [
      { title: "Ganga Aarti at Har Ki Pauri", description: "A spectacular daily ceremony with thousands of floating diyas on the Ganges at sunset." },
      { title: "Holy Dip in the Ganges", description: "Bathe at the Brahmakund—believed to wash away sins and purify the soul." },
      { title: "Shantikunj Ashram", description: "The headquarters of the Gayatri Parivar, offering meditation and spiritual workshops." },
      { title: "Mansa Devi Temple", description: "Ride the cable car to this hilltop temple with panoramic views of the city and river." },
    ],
    bestTimeToVisit: "February – April & September – November (pleasant weather, Kumbh years are special)",
    idealFor: ["Spiritual cleansing", "Ashram stays", "Hindu pilgrimage"],
    travelTips: [
      "Alcohol and non-vegetarian food are prohibited in Haridwar",
      "Wear modest clothing when visiting ghats and temples",
      "Combine with Rishikesh (25 km) for a complete spiritual experience",
    ],
    nearestAirport: "Jolly Grant Airport, Dehradun (38 km)",
  },
];

// Helper to find a destination by slug
export const getDestinationBySlug = (slug: string) =>
  destinationDetails.find((d) => d.slug === slug);

// Helper to get slug from location name
export const getSlugFromName = (name: string) => {
  const dest = destinationDetails.find((d) => d.name === name);
  return dest?.slug;
};
